import * as React from "react";
import * as PriceCalculator from "shared/price_calculator";
import * as Frontend from "kmmp/frontend";
import Paths from "shared/paths";
import { useLocation } from "wouter";
import { Cart, Auth, CartStore } from "client/stores";
import { Link } from "wouter";
import { Observer } from "mobx-react";
import PageHeader from "client/components/page-header";
import { PortraitPreview } from "client/components/portrait_preview";
import { getSampleFramePreviewImageUrl, getShadowBoxImage, getSignatureJerseyImage } from "client/image-urls";
import { getLineItemDescription } from "shared/get-line-item-description";
import { ExhaustiveCheckError } from "shared/errors";
import { If } from "shared/components";
import { Sentry } from "client/modules/sentry";

export function CartSummaryPage(): JSX.Element {
    const [_, setLocation] = useLocation();

    React.useEffect(() => {
        if (Auth.companySettings.siteType === "corporate") {
            setLocation(Paths.cart.name, { replace: true });
        }
    }, [Auth.companySettings.siteType]);

    if (Auth.companySettings.siteType === "corporate") {
        // Somehow people are still rendering this cart summary component despite being redirected away from it!
        return <div />;
    }

    return <EcommerceSummary store={Cart} />;
}

const findImageSrc = (li: Frontend.LineItem) => {
    switch (li.type) {
        case undefined:
        case "portrait":
        case "signature-portrait":
            const i = li.images[0];
            return i.croppedImage ? i.croppedImage.thumbnailUrl : i.fullImage.thumbnailUrl;

        case "shadow-box":
            return getShadowBoxImage(li.customization.color);

        case "signature-jersey":
            const packageType = li.customization.package;
            const { frame } = li.customization;
            return getSignatureJerseyImage(packageType, frame);

        case "sample-portraits":
            return getSampleFramePreviewImageUrl("Mahogany", "packages", false);

        default:
            throw new ExhaustiveCheckError(li);
    }
};

const getSubtotal = (li: Frontend.LineItem[]) =>
    li.reduce((value, li) => PriceCalculator.calculate(li.qty.packages, Auth.companySettings.products[0]) + value, 0);

const updateQuantity = (store: CartStore, li: Frontend.LineItem) => (event: React.FormEvent<HTMLInputElement>) =>
    store.setQuantity(li, "packages", event.currentTarget.valueAsNumber < 1 ? 1 : event.currentTarget.valueAsNumber);

type EcommerceProps = {
    store: CartStore;
}

const EcommerceSummary = ({ store }: EcommerceProps) => (
    <Observer>
        {() => (
            <div>
                <PageHeader title="Your Shopping Cart" />
                <section id="cart-summary" className="section white">
                    <div className="cart-table">
                        <div className="pure-g table-header">
                            <div className="pure-u-md-12-24 details ">{"Product"}</div>
                            <div className="pure-u-md-4-24 price">{"Price"}</div>
                            <div className="pure-u-md-4-24 quantity">{"Quantity"}</div>
                            <div className="pure-u-md-4-24 total">{"Total"}</div>
                        </div>
                        {store.items.map((li) => (
                            <div key={li.uuid} className="pure-g table-row">
                                <LineItemDetails item={li} onRemoveFromCart={store.removeItem} />
                                <div className="pure-u-md-4-24 price">
                                    {"$" + Auth.companySettings.products[0].price.toFixed(2)}
                                </div>
                                <If condition={li.type !== "sample-portraits"}>
                                    <div className="pure-u-md-4-24 quantity">
                                        <input
                                            type={"number"}
                                            value={li.qty.packages as number}
                                            onChange={updateQuantity(store, li)}
                                        />
                                    </div>
                                </If>
                                <div className="pure-u-md-4-24 total">
                                    {"$" +
                                        PriceCalculator.calculate(
                                            li.qty.packages,
                                            Auth.companySettings.products[0]
                                        ).toFixed(2)}
                                </div>
                            </div>
                        ))}
                        {store.items.length > 0 ? null : (
                            <div className="table-row text-center">{"Your cart is empty!"}</div>
                        )}
                    </div>
                    <div className="pricing-info">
                        <div className="pure-g">
                            <div className="pure-u-md-16-24 spacer" />
                            <div className="pure-u-md-4-24 subtotal-key">{"Subtotal:"}</div>
                            <div className="pure-u-md-4-24 subtotal-value">
                                {`$${getSubtotal(store.items.slice()).toFixed(2)}`}
                            </div>
                        </div>
                        <span className="subtotal-info">{"Shipping and taxes will be calculated at checkout."}</span>
                    </div>
                    <div className="buttons">
                        <Link className="btn" to={Paths.products.index}>
                            {"Continue Shopping"}
                        </Link>
                        <Link className="btn blue" to={Paths.checkout.index} onClick={() => Sentry.addBreadcrumb({
                            message: "User clicked Check Out from cart summary",
                            category: "Kmmp.Browser.Cart.Summary",
                            level: "info",
                            data: { itemCount: store.items.length },
                        })}>
                            {"Check Out"}
                        </Link>
                    </div>
                </section>
            </div>
        )}
    </Observer>
);

interface LineItemDetailsProps {
    item: Frontend.LineItem;
    onRemoveFromCart: (item: Frontend.LineItem) => void;
}

function LineItemDetails(props: LineItemDetailsProps): JSX.Element {
    const item = props.item;
    const imageSrc = findImageSrc(item);
    const onRemove = (e: React.MouseEvent<any>) => {
        e.preventDefault();
        props.onRemoveFromCart(item);
    };

    switch (item.type) {
        case undefined:
        case "portrait":
            return (
                <div className="pure-u-md-12-24 details">
                    <img src={imageSrc} alt={item.deceasedName} className="img-responsive" />
                    <PortraitPreview
                        whitelabelSettings={Auth.companySettings}
                        background={item.customization.background}
                        frame={item.customization.frame}
                        orientation={item.customization.orientation}
                        size={"16x20"}
                        isSignaturePortrait={false}
                        displayAsPackage={
                            Auth.companySettings.companyName === "sci" ||
                            Auth.companySettings.companyName === "messenger"
                        }
                    />
                    <div>
                        <div>{getLineItemDescription(item)}</div>
                        {item.deceasedName ? <div>{item.deceasedName}</div> : null}
                        <a href="#" className="remove-button" onClick={onRemove}>
                            {"Remove"}
                        </a>
                    </div>
                </div>
            );

        case "sample-portraits":
            return (
                <div className="pure-u-md-12-24 details">
                    <img src={imageSrc} alt="Sample Portraits" className="img-responsive" />
                    <div>
                        <div>{getLineItemDescription(item)}</div>
                        <a href="#" className="remove-button" onClick={onRemove}>
                            {"Remove"}
                        </a>
                    </div>
                </div>
            );

        case "signature-portrait":
        case "shadow-box":
        case "signature-jersey":
            return (
                <div className="pure-u-md-12-24 details">
                    <img src={imageSrc} alt={item.deceasedName} className="img-responsive" />
                    <div>
                        <div>{getLineItemDescription(item)}</div>
                        {item.deceasedName ? <div>{item.deceasedName}</div> : null}
                        <a href="#" className="remove-button" onClick={onRemove}>
                            {"Remove"}
                        </a>
                    </div>
                </div>
            );

        default:
            throw new ExhaustiveCheckError(item);
    }
}
